import { format, parseISO } from "date-fns";

/**
 * Format a date string to a more readable format
 * @param dateString The date string to format
 * @returns Formatted date string (e.g., "Jan 1, 2023 at 10:30 AM")
 */
export const formatDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    return format(date, "MM/dd/yyyy");
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return the original string if there's an error
  }
};

/**
 * Format a date string to a readable format without leading zeros in time
 * @param dateString The date string to format
 * @returns Formatted date string (e.g., "Jan 1, 2023 at 1:30 PM" instead of "01:30 PM")
 */
export const formatDateNoLeadingZeros = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    return format(date, "M/d/yyyy");
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return the original string if there's an error
  }
};

/**
 * Format a date to a simple date string (e.g., "Jan 1, 2023")
 * @param date The date to format
 * @returns Formatted date string
 */
export const formatSimpleDate = (date: Date): string => {
  try {
    return format(date, "MM/dd");
  } catch (error) {
    console.error("Error formatting simple date:", error);
    return date.toLocaleDateString();
  }
};

/**
 * Format a date to include month, day, and year
 * @param date The date to format
 * @returns Formatted date string with year (e.g., "MM/DD/YYYY")
 */
export const formatDateWithYear = (date: Date): string => {
  try {
    return format(date, "MM/dd/yyyy");
  } catch (error) {
    console.error("Error formatting date with year:", error);
    return date.toLocaleDateString();
  }
};

/**
 * Get a formatted string for today's date
 * @returns Formatted string for today's date
 */
export const getTodayFormatted = (): string => {
  return formatSimpleDate(new Date());
};

/**
 * Formats a date string to a user-friendly format with time (MM/DD/YYYY, h:mm a)
 * @param dateString ISO date string
 * @returns Formatted date and time string
 */
export const formatDateTime = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    return format(date, "MM/dd/yyyy, h:mm a");
  } catch (error) {
    console.error("Error formatting date time:", error);
    return dateString;
  }
};
